import { useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal, TeamMember, DealStage } from "../../lib/types";
import { notifyChadOnBuildApproval } from "../../lib/notifyChadOnBuildApproval";
import { notifyShannonOnSupplementRelease } from "../../lib/notifyShannonOnSupplementRelease";
import { notifyHomeownerPaymentDue } from "../../lib/notifyHomeownerPaymentDue";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { passToNotifyPlan, useBuildSubmitGate } from "./BuildPaymentPlanModal";

// ---------------------------------------------------------------------------
// Stage display labels + grouping
// ---------------------------------------------------------------------------

const STAGE_GROUPS: Array<{ label: string; stages: Array<{ value: DealStage; label: string }> }> = [
  {
    label: "Drone",
    stages: [
      { value: "pending_asm_review", label: "Pending ASM Review" },
      { value: "appointment_received", label: "Appointment Received" },
      { value: "drone_scheduled", label: "Drone Scheduled" },
      { value: "drone_complete", label: "Drone Complete" },
      { value: "report_in_progress", label: "Report In Progress" },
      { value: "qc_review", label: "QC Review" },
      { value: "report_approved", label: "Report Approved" },
      { value: "needs_climb_inspection", label: "Needs Climb Inspection" },
      { value: "insufficient_damage", label: "Insufficient Damage" },
    ],
  },
  {
    label: "Leads",
    stages: [
      { value: "initial_contact", label: "Initial Contact" },
      { value: "presentation_scheduled", label: "Presentation Scheduled" },
      { value: "follow_up", label: "Follow Up" },
      { value: "waiting", label: "Waiting" },
    ],
  },
  {
    label: "Claims",
    stages: [
      { value: "assign_roof_specialist", label: "Assign Roof Specialist" },
      { value: "pre_inspection_ready", label: "Pre-Inspection Ready" },
      { value: "pre_inspection_complete", label: "Pre-Inspection Complete" },
      { value: "adjuster_meeting_ready", label: "Adjuster Meeting Ready" },
      { value: "waiting_on_claims", label: "Waiting on Claims" },
      { value: "claims_denied", label: "Claims Denied" },
      { value: "partial_approval", label: "Partial Approval" },
      { value: "docs_and_contingency", label: "Docs & Contingency" },
      { value: "reinspection_ready", label: "Reinspection Ready" },
      { value: "reinspection_filed", label: "Reinspection Filed" },
    ],
  },
  {
    label: "Builds",
    stages: [
      { value: "install_meeting", label: "Install Meeting" },
      { value: "build_submitted", label: "Build Submitted" },
      { value: "schedule_build", label: "Schedule Build" },
      { value: "build_scheduled", label: "Build Scheduled" },
      // roof_complete retired May 7, 2026 — build-complete now sends straight to ready_for_supps
      { value: "trades_in_progress", label: "Trades In Progress" },
      { value: "ready_for_supps", label: "Ready for Supps" },
      { value: "supps_waiting_on_claims", label: "Supps Waiting on Claims" },
    ],
  },
  {
    label: "Closing",
    stages: [
      { value: "collecting", label: "Collecting" },
      { value: "build_complete", label: "Build Complete" },
      { value: "trades_complete", label: "Trades Complete" },
      { value: "collect_payment", label: "Collect Payment" },
      { value: "commission_ready", label: "Commission Ready" },
      { value: "commission_approved", label: "Commission Approved" },
      { value: "commission_paid", label: "Commission Paid" },
      { value: "closed_won", label: "Closed Won" },
    ],
  },
  {
    label: "Lost / Other",
    stages: [
      { value: "no_thanks", label: "No Thanks" },
      { value: "closed_lost", label: "Closed Lost" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
];

function stageLabel(stage: DealStage | null): string {
  if (!stage) return "—";
  for (const group of STAGE_GROUPS) {
    const found = group.stages.find((s) => s.value === stage);
    if (found) return found.label;
  }
  return stage;
}

/**
 * Stages that come AFTER Chad's build-approval gate. Overriding directly
 * into one of these without the normal ChadApprovalsTab approve flow
 * leaves `agreement_status` stale (null / pending_approval / needs_revision)
 * which then makes the deal look orphan'd in any UI that reads agreement_status.
 *
 * The fix: when an override targets one of these stages, bump
 * `agreement_status` to "approved" in the same DB write so downstream
 * surfaces stay consistent.
 */
const POST_APPROVAL_STAGES = new Set<DealStage>([
  "schedule_build",
  "build_scheduled",
  "roof_complete",
  "trades_in_progress",
  "ready_for_supps",
  "supps_waiting_on_claims",
  "collecting",
  "build_complete",
  "trades_complete",
  "collect_payment",
  "commission_ready",
  "commission_approved",
  "commission_paid",
  "closed_won",
]);

// ---------------------------------------------------------------------------
// Roles allowed to use Admin Controls
// ---------------------------------------------------------------------------

const ADMIN_ROLES = new Set([
  "admin",
  "sales_director",
  "asm",
  "team_lead",
  "roof_director",
  "director_of_operations",
]);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  deal: Deal;
  members: Record<string, TeamMember>;
  member: TeamMember | null;
  role: string | undefined;
  isSuperUser: boolean;
  reload: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminControlsPanel({ deal, members, member, role, isSuperUser, reload }: Props) {
  const canUse = isSuperUser || (!!role && ADMIN_ROLES.has(role));
  // Hook must be declared before any early return (Rules of Hooks).
  const [open, setOpen] = useState(false);

  if (!canUse) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden">
      {/* Header toggle */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-amber-100/60 transition"
      >
        <div className="flex items-center gap-2">
          <span className="text-amber-600 text-[13px]">⚙</span>
          <span className="text-[12px] font-semibold text-amber-800 tracking-wide uppercase">Admin Controls</span>
        </div>
        <span className="text-amber-500 text-[13px]">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="border-t border-amber-200 divide-y divide-amber-200">
          <StageOverrideSection deal={deal} member={member} reload={reload} />
          <RmReassignSection deal={deal} members={members} member={member} reload={reload} />
          <SendPayLinkSection deal={deal} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage Override
// ---------------------------------------------------------------------------

function StageOverrideSection({
  deal,
  member,
  reload,
}: {
  deal: Deal;
  member: TeamMember | null;
  reload: () => Promise<void>;
}) {
  const [newStage, setNewStage] = useState<DealStage | "">("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // An override into the build stages from an earlier stage submits the build
  // (Oct 21, 2026), so it asks for the first payment plan like every other path.
  const gate = useBuildSubmitGate();

  const currentLabel = stageLabel(deal.stage);
  const canSave = !!newStage && newStage !== deal.stage && reason.trim().length >= 3;

  async function handleOverride() {
    if (!canSave || !newStage) return;
    setSaving(true);
    try {
      // If the target stage sits past Chad's build-approval gate, also flip
      // agreement_status → "approved" so the deal doesn't look orphan'd in
      // downstream views that key off agreement_status (e.g. an old badge
      // query, AgreementReviewModal, finance reads). If it's already
      // "approved" or we're moving to a pre-approval stage, leave it alone.
      const bumpAgreement =
        POST_APPROVAL_STAGES.has(newStage) && deal.agreement_status !== "approved";

      const updates: Record<string, unknown> = { stage: newStage, days_in_stage: 0 };
      if (bumpAgreement) updates.agreement_status = "approved";

      const pass = await gate.run(
        { dealId: deal.id, homeowner: deal.homeowner_name, fromStage: deal.stage, toStage: newStage, source: "admin_override", title: "Submit build", actionLabel: `Move to ${stageLabel(newStage)}` },
        async () => {
          const { error } = await supabase
            .from("deals")
            .update(updates)
            .eq("id", deal.id);
          if (error) throw error;
        },
      );
      if (!pass) return; // cancelled

      // Audit log
      await supabase.from("activity_log").insert({
        deal_id: deal.id,
        activity_type: "stage_override",
        title: "Admin stage override",
        description:
          `Stage moved from "${currentLabel}" → "${stageLabel(newStage)}" by ${member?.full_name ?? "admin"}.` +
          (bumpAgreement ? ` agreement_status auto-set to "approved" (post-approval stage).` : "") +
          ` Reason: ${reason.trim()}`,
        metadata: {
          from_stage: deal.stage,
          to_stage: newStage,
          reason: reason.trim(),
          by: member?.full_name ?? null,
          by_id: member?.id ?? null,
          agreement_status_bumped: bumpAgreement,
        },
      });

      // Text Chad if an admin override puts the deal in his build-approval queue.
      // Mirrors the same hook in TransitionButton so all UI paths into
      // build_submitted send the alert.
      if (newStage === "build_submitted" && deal.stage !== "build_submitted") {
        void notifyChadOnBuildApproval(deal, { actorId: member?.id, plan: passToNotifyPlan(pass) });
      }
      // Notify Shannon when an admin override puts a deal into supplements.
      if (newStage === "ready_for_supps" && deal.stage !== "ready_for_supps") {
        void notifyShannonOnSupplementRelease(deal);
      }

      setNewStage("");
      setReason("");
      await reload();
    } catch (err: unknown) {
      alert("Stage override failed: " + formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-4 py-3 space-y-2">
      {gate.element}
      <div className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider">Stage Override</div>
      <div className="text-[11px] text-slate-500">
        Current: <span className="font-medium text-slate-700">{currentLabel}</span>
      </div>

      <div className="space-y-2">
        <select
          value={newStage}
          onChange={(e) => setNewStage(e.target.value as DealStage | "")}
          className="w-full text-[12px] border border-amber-200 rounded-lg px-3 py-2 bg-white text-slate-800"
        >
          <option value="">Move to stage…</option>
          {STAGE_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.stages.map((s) => (
                <option
                  key={s.value}
                  value={s.value}
                  disabled={s.value === deal.stage}
                >
                  {s.label}{s.value === deal.stage ? " (current)" : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for override (required)"
          className="w-full text-[12px] border border-amber-200 rounded-lg px-3 py-2 bg-white text-slate-800 placeholder-slate-400"
        />

        <button
          onClick={handleOverride}
          disabled={!canSave || saving}
          className="w-full py-2 rounded-lg text-[12px] font-semibold transition
            bg-amber-500 text-white hover:bg-amber-600
            disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          {saving ? "Overriding…" : "Override Stage"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RM Reassign
// ---------------------------------------------------------------------------

function RmReassignSection({
  deal,
  members,
  member,
  reload,
}: {
  deal: Deal;
  members: Record<string, TeamMember>;
  member: TeamMember | null;
  reload: () => Promise<void>;
}) {
  const [newRmId, setNewRmId] = useState<string>("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Active deal-carrying members (RM / TL / ASM)
  const dealCarriers = Object.values(members)
    .filter((m) => m.is_active && ["restore_manager", "team_lead", "asm"].includes(m.role))
    .sort((a, b) => a.full_name.localeCompare(b.full_name));

  const currentOwnerName = deal.owner_id ? members[deal.owner_id]?.full_name ?? "Unassigned" : "Unassigned";
  const newRmName = newRmId ? members[newRmId]?.full_name ?? "" : "";
  const canSave = !!newRmId && newRmId !== deal.owner_id;

  async function handleReassign() {
    if (!canSave) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("deals")
        .update({ owner_id: newRmId })
        .eq("id", deal.id);
      if (error) throw error;

      // Audit log
      await supabase.from("activity_log").insert({
        deal_id: deal.id,
        activity_type: "rm_reassign",
        title: "RM reassigned",
        description: `RM changed from "${currentOwnerName}" → "${newRmName}" by ${member?.full_name ?? "admin"}${note.trim() ? `. Note: ${note.trim()}` : ""}`,
        metadata: {
          from_rm_id: deal.owner_id,
          from_rm_name: currentOwnerName,
          to_rm_id: newRmId,
          to_rm_name: newRmName,
          note: note.trim() || null,
          by: member?.full_name ?? null,
          by_id: member?.id ?? null,
        },
      });

      // Notify Chad (sales director)
      const chad = Object.values(members).find((m) => m.email === "chad@bardinroofing.com");
      if (chad) {
        await supabase.from("notifications").insert({
          user_id: chad.id,
          type: "rm_reassign",
          title: `RM reassigned on ${deal.homeowner_name ?? "a deal"}`,
          body: `${currentOwnerName} → ${newRmName}${note.trim() ? ` · ${note.trim()}` : ""} (by ${member?.full_name ?? "admin"})`,
          deal_id: deal.id,
          metadata: {
            from_rm: currentOwnerName,
            to_rm: newRmName,
            by: member?.full_name ?? null,
            note: note.trim() || null,
          },
        });
      }

      // Also notify the new RM
      if (newRmId !== member?.id) {
        await supabase.from("notifications").insert({
          user_id: newRmId,
          type: "rm_reassign",
          title: `Deal assigned to you`,
          body: `${deal.homeowner_name ?? "A deal"} at ${deal.property_street ?? "unknown address"} was assigned to you by ${member?.full_name ?? "admin"}${note.trim() ? `. Note: ${note.trim()}` : ""}`,
          deal_id: deal.id,
          metadata: {
            from_rm: currentOwnerName,
            by: member?.full_name ?? null,
            note: note.trim() || null,
          },
        });
      }

      setNewRmId("");
      setNote("");
      await reload();
    } catch (err: unknown) {
      alert("Reassign failed: " + formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-4 py-3 space-y-2">
      <div className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider">Reassign RM</div>
      <div className="text-[11px] text-slate-500">
        Current RM: <span className="font-medium text-slate-700">{currentOwnerName}</span>
      </div>

      <div className="space-y-2">
        <select
          value={newRmId}
          onChange={(e) => setNewRmId(e.target.value)}
          className="w-full text-[12px] border border-amber-200 rounded-lg px-3 py-2 bg-white text-slate-800"
        >
          <option value="">Reassign to…</option>
          {dealCarriers.map((m) => (
            <option
              key={m.id}
              value={m.id}
              disabled={m.id === deal.owner_id}
            >
              {m.full_name}{m.id === deal.owner_id ? " (current)" : ""}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (e.g. RM left the company)"
          className="w-full text-[12px] border border-amber-200 rounded-lg px-3 py-2 bg-white text-slate-800 placeholder-slate-400"
        />

        <button
          onClick={handleReassign}
          disabled={!canSave || saving}
          className="w-full py-2 rounded-lg text-[12px] font-semibold transition
            bg-amber-500 text-white hover:bg-amber-600
            disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          {saving ? "Reassigning…" : `Reassign${newRmName ? ` to ${newRmName}` : ""}`}
        </button>

        {canSave && (
          <div className="text-[10px] text-slate-400">
            Chad will be notified of this change. Commission is case-by-case.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send Pay Link to Homeowner — May 8, 2026 (Greg's "99/100 pay through portal")
// ---------------------------------------------------------------------------

function SendPayLinkSection({ deal }: { deal: Deal }) {
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  async function handleSend() {
    setSending(true);
    setResult(null);
    try {
      const r = await notifyHomeownerPaymentDue(deal as unknown as Parameters<typeof notifyHomeownerPaymentDue>[0]);
      if (r.ok && r.sent) {
        setResult({ ok: true, msg: `Sent to ${deal.homeowner_phone}` });
      } else if (r.ok && !r.sent) {
        setResult({ ok: false, msg: `Skipped: ${r.reason ?? "no reason given"}` });
      } else {
        setResult({ ok: false, msg: `Failed: ${r.reason ?? "unknown error"}` });
      }
    } catch (err) {
      setResult({ ok: false, msg: formatSupabaseError(err) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="px-4 py-3 space-y-2">
      <div className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider">
        Send Pay Link to Homeowner
      </div>
      <div className="text-[11px] text-slate-500">
        Texts the homeowner their portal payment link with the live amount due.
        Use when the auto-text didn't fire (older deal, RM did the install
        meeting before this feature existed, etc).
      </div>
      <button
        onClick={handleSend}
        disabled={sending || !deal.homeowner_phone}
        className="rounded-md bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white text-[12px] font-semibold px-3 py-1.5"
      >
        {sending ? "Sending…" : "📱 Send pay link"}
      </button>
      {!deal.homeowner_phone && (
        <div className="text-[10px] text-slate-400">No phone on file — can't send.</div>
      )}
      {result && (
        <div className={`text-[11px] ${result.ok ? "text-emerald-700" : "text-red-600"}`}>
          {result.ok ? "✓" : "⚠"} {result.msg}
        </div>
      )}
    </div>
  );
}
